<template>
    <div>
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless class="list">
            <b-thead>
                <b-tr>
                    <b-th style="width: 3%;"></b-th>
                    <b-th style="width: 10%;">Date:</b-th>
                    <b-th style="width: 46%;">Communication:</b-th>
                    <b-th style="width: 10%;">Patient:</b-th>
                    <b-th style="width: 8.5%;">Attachments:</b-th>
                    <b-th style="width: 8.5%;">Add By:</b-th>
                    <b-th style="width: 7%;">Status:</b-th>
                    <b-th style="width: 7%;"></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="(comm, i) in comms" :key="i">
                    <b-td style="text-align: center;">
                        <i
                            :class="iconType(comm)"
                            v-b-tooltip.hover.top
                            :title="commTypeTitle(comm)"
                        />
                    </b-td>
                    <b-td>
                        {{ _carbonDateTimeDisplay(comm.date) }}
                        <input type="hidden" name="id" :value="comm.id" />
                    </b-td>
                    <b-td>
                        <html-content
                            :isReplaceNewLine="comm.type !== 4"
                            :content="formatContent(comm.content)"
                            :expanded="currentExpandedCommId === comm.id"
                            :maxWidth="650"
                            @onClickSeeMore="() => expandContent(comm.id)"
                            @onClickSeeLess="() => expandContent(null)"
                        />
                    </b-td>
                    <b-td>
                        <patient-name
                            :use-router="USE_PATIENT_MODULE_VUE"
                            v-if="comm.patient"
                            :patient="comm.patient"
                        />
                        <span v-else>-</span>
                    </b-td>
                    <b-td>
                        <a
                            v-if="comm.reference"
                            :href="CI_APPS + comm.reference.ci_url"
                            target="_blank"
                        >
                            {{ comm.reference.title }}
                        </a>
                        <div v-html="extractBccAttachment(comm.content)"></div>
                    </b-td>
                    <b-td>
                        {{ comm.create_by ? comm.create_by.label : null }}
                        <div v-if="!isCreator(comm.business_id)">
                            ({{ comm.business.label }})
                        </div>
                    </b-td>
                    <b-td>
                        <template v-if="comm.groups && comm.groups.length">
                            <div
                                v-for="communication in comm.groups"
                                :key="communication.id"
                            >
                                <communication-status
                                    class="mb-1"
                                    :status="communication.status"
                                    :smtp-error="communication.smtp_error"
                                    :contact="communication.contact"
                                />
                            </div>
                        </template>
                        <communication-status
                            v-else
                            :status="comm.status"
                            :smtp-error="comm.smtp_error"
                            :contact="comm.actor_contact"
                        />
                    </b-td>
                    <b-td class="options">
                        <!-- delete & edit -->
                        <b-dropdown
                            v-if="
                                isCreator(comm.business_id) &&
                                comm.iseditable &&
                                (_hasAccess('client.communication.delete') ||
                                    _hasAccess('client.communication.update'))
                            "
                            size="sm"
                            variant="link"
                            right
                            no-caret
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>
                            <b-dropdown-item
                                v-if="_hasAccess('client.communication.update')"
                                @click="editCommunication(comm)"
                            >
                                <i class="cus-page-white-edit"></i>
                                Edit Communication
                            </b-dropdown-item>
                            <b-dropdown-item
                                v-if="_hasAccess('client.communication.delete')"
                                @click="promptDelete(comm.id)"
                            >
                                <i class="cus-delete"></i>
                                Delete Communication
                            </b-dropdown-item>
                        </b-dropdown>
                        <!-- delete for not editable comm -->
                        <b-dropdown
                            v-if="
                                isCreator(comm.business_id) &&
                                !comm.iseditable &&
                                allowResend(comm) &&
                                (_hasAccess('client.communication.delete') ||
                                    _hasAccess('client.communication.create'))
                            "
                            size="sm"
                            variant="link"
                            right
                            no-caret
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>
                            <b-dropdown-item
                                v-if="_hasAccess('client.communication.create')"
                                @click="resendCommunication(comm)"
                            >
                                <i class="cus-email-go"></i>
                                Resend Communication
                            </b-dropdown-item>
                            <b-dropdown-item
                                v-if="_hasAccess('client.communication.delete')"
                                @click="promptDelete(comm.id)"
                            >
                                <i class="cus-delete"></i>
                                Delete Communication
                            </b-dropdown-item>
                        </b-dropdown>
                        <b-button
                            v-if="
                                isCreator(comm.business_id) &&
                                !comm.iseditable &&
                                !allowResend(comm) &&
                                _hasAccess('client.communication.delete')
                            "
                            right
                            variant="link"
                            size="sm"
                            class="p-0"
                            @click="promptDelete(comm.id)"
                        >
                            <i class="cus-delete"></i>
                        </b-button>
                    </b-td>
                </b-tr>
                <b-tr v-if="comms.length === 0">
                    <b-td colspan="8">
                        <i>No records available</i>
                    </b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="showPagination"
            class="vet-pagination"
            align="right"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="changePage"
        />

        <modal-delete ref="modalDelete" @onSuccess="afterDeleted" />

        <modal-content ref="modalContent" />
    </div>
</template>

<script>
import ClientDocument from "@/services/client-documents";
import ClientCommunication from "@/services/client-communications";
import Loader from "@/components/elements/loader-list";
import ModalDelete from "@/components/communication/communication-delete-modal";
import PatientName from "@/components/commons/patient-name";
import { DateTime } from "luxon";
import ModalContent from "./communication-content-modal";
import HtmlContent from "@/components/commons/html-content";
import CommunicationStatus from "./communication-status";
import CommunicationHelper from "@/module/communication/helper"; // helper
import EXTENSIONS from "@/constants/extensions";
import COMMUNICATION from "@/constants/communications";
import COMMUNICATIONTYPE from "@/constants/communication-types";

export default {
    mixins: {
        CommunicationHelper
    },
    components: {
        Loader,
        ModalDelete,
        PatientName,
        ModalContent,
        HtmlContent,
        CommunicationStatus
    },
    props: {
        listType: {
            type: String,
            default: "all"
        }
    },
    data() {
        return {
            comms: [],
            resultMeta: {},
            isLoading: false,
            clientId: this.$store.state.client.client_id,
            isLoadingPrintRecordId: false,
            CI_APPS: process.env.CI_APPS,
            currentPage: 1,
            totalRows: 0,
            perPage: 0,
            showPagination: false,
            currentExpandedCommId: null,
            COMMUNICATION: COMMUNICATION
        };
    },
    computed: {
        userId() {
            return this.$route.params.id;
        }
    },
    mounted() {
        this.getCommunications(1);
    },
    methods: {
        async editLetter(documentId) {
            const encodedId = await ClientDocument.getLetterEncodedId(
                documentId
            );
            window.location.href = `${process.env.CI_APPS}patient/letter/view/${encodedId}/1`;
        },
        editAttachment(attachment) {
            let params = {
                action: "edit",
                attachment: attachment,
                module: this.MODULE.CLIENT,
                client_id: this.$store.state.client.client_id
            };
            this.$refs.modalAddAttachment.open(params);
        },
        async getCommunications(page) {
            this.isLoading = true;
            let clientId = this.$store.state.client.client_id;
            let res = await ClientCommunication.getCommunications(
                clientId,
                page
            );
            let { meta, resources } = res;

            this.resultMeta = meta;
            this.comms = resources;
            this.isLoading = false;

            // update pagination details
            this.currentPage = meta.currentPage;
            this.totalRows = meta.total;
            this.perPage = meta.perPage;
            this.showPagination = meta.lastPage > 1;
        },
        changePage(nextPage) {
            this.getCommunications(nextPage);
        },
        promptDelete(recordId) {
            this.$refs.modalDelete.open(recordId);
        },
        afterDeleted() {
            this.getCommunications();
        },
        async printLetter(documentId) {
            this.isLoadingPrintRecordId = documentId;
            const pdfPath = await ClientDocument.getPDFLetterURI(documentId);
            this.isLoadingPrintRecordId = null;

            if (pdfPath) {
                // eslint-disable-next-line no-undef
                window.open(path, "_blank");
            } else {
                this._message("Error: Unable to print", "danger");
            }
        },
        editCommunication(comm) {
            // how many "$parent" should we use?
            // open vue developer tools, search the component and count till we find it
            this.$parent.$parent.$parent.$parent.$refs[
                "modalLogCommunication"
            ].open(comm);
        },
        resendCommunication(comm) {
            this.$root.$emit("resend-communication", comm);
        },
        openModalContent(content) {
            this.$refs.modalContent.open(content);
        },
        expandContent(commId) {
            this.currentExpandedCommId = commId;
        },
        iconType(comm) {
            const icons = {
                1: "phone", // phone
                2: "user_comment", // in person
                3: "printer", // fax
                4: "email", // email
                5: "email_edit", // mail
                6: "comment", // sms
                7: "webcam" // telehealth
            };
            if (comm.master_communication_type) {
                return `famfamfam-silk ${
                    icons[comm.master_communication_type.id] || "comments"
                }`;
            } else {
                return `famfamfam-silk ${icons[6]}`;
            }
        },
        allowResend(comm) {
            // the function called from mixins/communication.js
            return this.checkAllowResend({
                typeId: comm?.master_communication_type?.id || null,
                businessId: comm.business_id
            });
        },

        checkAllowResend({ typeId, businessId }) {
            // Will use the mixins/communication later, currently this component cannot add more mixins as the mixin @/module/communication/helper been imported in wrong way, should refactor to use export default in the mixin and change from {} to [] to call mixins
            let allow = false;
            if (typeId === COMMUNICATIONTYPE.COMMUNICATION_TYPES.EMAIL) {
                // email
                allow = true;
            } else if (
                // SMS
                typeId === COMMUNICATIONTYPE.COMMUNICATION_TYPES.SMS &&
                (this._isExtensionEnabled(EXTENSIONS.CODE.CODE_SMS) ||
                    this._isExtensionEnabled(
                        EXTENSIONS.CODE.CODE_2_WAY_MESSAGING
                    ))
            ) {
                allow = true;
            }

            return (
                allow &&
                this._hasAccess("client.communication.create") &&
                businessId === this._setting.business_id
            );
        },
        commTypeTitle(comm) {
            if (comm.master_communication_type) {
                return comm.master_communication_type.value;
            }

            return "";
        },
        extractBccAttachment(html) {
            let hasAttachment = html.includes(`<div id="bcc_attachment">`);

            if (hasAttachment) {
                let index = html.indexOf(`<div id="bcc_attachment">`);
                let str = html.substr(index);

                let arr = $(str).find("a").toArray();
                let result = [];

                for (const element of arr) {
                    result.push(element.outerHTML);
                }

                return result.join("<br/>");
            }
        },
        isCreator(business_id) {
            return business_id === this._setting.business_id;
        }
    }
};
</script>

<style scoped>
td.options {
    width: 50px;
    text-align: right;
}
</style>
